import axios, { type AxiosResponse } from 'axios';
import { logger } from '@trading-utils/logger';

import { processBackendError } from '@/utils/error.utils';

import i18n from '@/i18n/config';

import type { ConsumptionChartResponse, ConsumptionTableResponse } from '@/interfaces';

export const getConsumerTableData = async (url: string): Promise<ConsumptionTableResponse> => {
  try {
    const response: AxiosResponse<ConsumptionTableResponse> = await axios.get(url);

    logger.info(`Fetched consumption table data from ${url}`);

    return response.data;
  } catch (error) {
    logger.error('Error fetching consumption table data', { url, error });
    throw processBackendError(error, i18n.t('errors.consumer.errorConsumerTable'));
  }
};

export const getConsumptionChartData = async (url: string): Promise<ConsumptionChartResponse> => {
  try {
    const response: AxiosResponse<ConsumptionChartResponse> = await axios.get(url);

    logger.info(`Fetched consumption chart data from ${url}`);

    return response.data;
  } catch (error) {
    logger.error('Error fetching consumption chart data', { url, error });
    throw processBackendError(error, i18n.t('errors.consumer.errorConsumerChart'));
  }
};

export const updateConsumptions = async (
  updates: Array<{
    id: number;
    nomination: number | null;
    plantId: number;
    date: Date;
  }>,
  url: string
): Promise<ConsumptionTableResponse> => {
  try {
    const response: AxiosResponse<ConsumptionTableResponse> = await axios.patch(url, updates);

    return response.data;
  } catch (error) {
    logger.error('Error updating consumption', { url, error });
    throw processBackendError(error, i18n.t('errors.consumer.errorUpdateConsumptions'));
  }
};

export const exportConsumptions = async (url: string): Promise<{ blob: Blob; filename: string }> => {
  try {
    const response: AxiosResponse<Blob> = await axios.get(url, { responseType: 'blob' });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'consumptions.xlsx'; // Default fallback

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    return { blob: response.data, filename };
  } catch (error) {
    logger.error('Error exporting consumptions', { url, error });
    throw processBackendError(error, i18n.t('errors.consumer.errorExportConsumptions'));
  }
};
